import React, { createContext, useContext, ReactNode } from 'react'
import { ethers } from 'ethers'
import { LB_ROUTER_V22_ADDRESS } from '@traderjoe-xyz/sdk-v2'
import { useOtChainState } from '@react/redux/store'
import { ChainId } from '@shared/Enums'
import { getSwapTokenConfig, TokenMetadata } from '@shared/SwapTokenConfig'

// Import RPC URLs from deployment configs
const PROD_AVALANCHE_FRONTEND_URL = 'https://avax-mainnet.g.alchemy.com/v2/p5dgOkAqxryvxryB4To24'
const QA_FUJI_BACKEND_URL = 'https://avax-fuji.g.alchemy.com/v2/_ESugHwjHIW_-ezv1xUvy'

export interface SwapNetworkConfig {
  chainId: number
  name: string
  rpcUrl: string
  tokens: {
    TOKEN_A: TokenMetadata
    TOKEN_B: TokenMetadata
  }
  dexPools: {
    TOKEN_A_TOKEN_B_POOL: string
  }
  routerAddress: string
  availableTokens: TokenMetadata[]
}

function createSwapNetworkConfig(stageName: string, chainId: number): SwapNetworkConfig {
  const tokenConfig = getSwapTokenConfig(stageName, chainId)

  const rpcUrl = chainId === 43114 ? PROD_AVALANCHE_FRONTEND_URL : QA_FUJI_BACKEND_URL

  const name = chainId === 43114 ? 'Avalanche Mainnet' : 'Avalanche Fuji Testnet'

  return {
    chainId,
    name,
    rpcUrl,
    tokens: {
      TOKEN_A: tokenConfig.TOKEN_A,
      TOKEN_B: tokenConfig.TOKEN_B,
    },
    dexPools: tokenConfig.dexPools,
    routerAddress: LB_ROUTER_V22_ADDRESS[chainId as keyof typeof LB_ROUTER_V22_ADDRESS],
    availableTokens: tokenConfig.availableTokens,
  }
}

export const LYT_SWAP_CONFIG = {
  OKX_ENABLED_STAGES: ['localhost', 'localsierra', 'qasierra', 'prodsierra', 'prodsierracanary'],
  OKX_SUPPORTED_CHAINS: [ChainId.AVALANCHE],
  LFJ_ENABLED_STAGES: ['devsierra', 'prod'],
  NETWORK_SWITCHING_ENABLED_STAGES: ['local', 'devsierra'],

  POOL_CONFIG: {
    GAS_ESTIMATE: {
      APPROVE: 50000n,
      SWAP: 300000n,
    },
  },

  isOkxAvailable: (stageName: string, chainId: number) => {
    return (
      LYT_SWAP_CONFIG.OKX_ENABLED_STAGES.includes(stageName) && LYT_SWAP_CONFIG.OKX_SUPPORTED_CHAINS.includes(chainId)
    )
  },

  isLfjAvailable: (stageName: string, _chainId: number) => {
    return LYT_SWAP_CONFIG.LFJ_ENABLED_STAGES.includes(stageName)
  },
}

export const LytConstants = LYT_SWAP_CONFIG

interface SwapNetworkContext {
  provider: ethers.JsonRpcProvider
  config: SwapNetworkConfig
  switchToSwapNetwork: () => Promise<boolean>
  canSwitchNetworks: boolean
  availableNetworks: SwapNetworkConfig[]
  switchToNetwork: (chainId: number) => Promise<boolean>
}

const SwapNetworkContext = createContext<SwapNetworkContext | null>(null)

export function SwapNetworkProvider({ children }: { children: ReactNode }) {
  const {
    chainConfiguration: { chainId, frontendProviderUrl },
    stageConfiguration: { stageName },
  } = useOtChainState()

  const canSwitchNetworks = LYT_SWAP_CONFIG.NETWORK_SWITCHING_ENABLED_STAGES.includes(stageName)
  const isOkxOnlyStage =
    LYT_SWAP_CONFIG.OKX_ENABLED_STAGES.includes(stageName) && !LYT_SWAP_CONFIG.LFJ_ENABLED_STAGES.includes(stageName)

  const createNetworkConfigSafely = (chain: number): SwapNetworkConfig => {
    try {
      return createSwapNetworkConfig(stageName, chain)
    } catch (_error) {
      const fallbackStage = chain === ChainId.FUJI ? 'qasierrafb' : 'qasierra'
      console.warn(`FALLBACK: Swap config not found for ${stageName}-${chain}. Using ${fallbackStage}-${chain}.`)
      return createSwapNetworkConfig(fallbackStage, chain)
    }
  }

  const networkConfigs = new Map<number, SwapNetworkConfig>()

  const chainsNeeded = new Set<number>()
  if (canSwitchNetworks) {
    chainsNeeded.add(ChainId.FUJI)
    chainsNeeded.add(ChainId.AVALANCHE)
  } else if (isOkxOnlyStage) {
    chainsNeeded.add(ChainId.AVALANCHE)
  } else {
    chainsNeeded.add(chainId)
  }

  chainsNeeded.forEach((chain) => {
    networkConfigs.set(chain, createNetworkConfigSafely(chain))
  })

  const availableNetworks = Array.from(networkConfigs.values())

  const currentNetworkConfig =
    networkConfigs.get(chainId) || networkConfigs.get(ChainId.AVALANCHE) || networkConfigs.values().next().value

  if (!currentNetworkConfig) {
    throw new Error(`No swap network config available for stage: ${stageName}, chainId: ${chainId}`)
  }
  const providerUrl = frontendProviderUrl || currentNetworkConfig.rpcUrl
  const provider = new ethers.JsonRpcProvider(providerUrl)

  const switchToSwapNetwork = async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !window.ethereum) {
      console.error('MetaMask not available')
      return false
    }

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${currentNetworkConfig.chainId.toString(16)}` }],
      })

      return true
    } catch (switchError: any) {
      if (switchError.code === 4902) {
        try {
          const networkParams = {
            chainId: `0x${currentNetworkConfig.chainId.toString(16)}`,
            chainName: currentNetworkConfig.name,
            rpcUrls: [currentNetworkConfig.rpcUrl],
            nativeCurrency: {
              name: 'AVAX',
              symbol: 'AVAX',
              decimals: 18,
            },
            blockExplorerUrls: [
              currentNetworkConfig.chainId === ChainId.AVALANCHE
                ? 'https://snowtrace.io/'
                : 'https://testnet.snowtrace.io/',
            ],
          }

          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [networkParams],
          })
          return true
        } catch (addError) {
          console.error('Failed to add network:', addError)
          return false
        }
      }
      console.error('Failed to switch network:', switchError)
      return false
    }
  }

  const switchToNetwork = async (targetChainId: number): Promise<boolean> => {
    if (!canSwitchNetworks) {
      console.warn('Network switching is not enabled for this environment')
      return false
    }

    if (typeof window === 'undefined' || !window.ethereum) {
      console.error('MetaMask not available')
      return false
    }

    const targetConfig = availableNetworks.find((network) => network.chainId === targetChainId)
    if (!targetConfig) {
      console.error('Target network not available')
      return false
    }

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${targetConfig.chainId.toString(16)}` }],
      })

      return true
    } catch (switchError: any) {
      if (switchError.code === 4902) {
        try {
          const networkParams = {
            chainId: `0x${targetConfig.chainId.toString(16)}`,
            chainName: targetConfig.name,
            rpcUrls: [targetConfig.rpcUrl],
            nativeCurrency: {
              name: 'AVAX',
              symbol: 'AVAX',
              decimals: 18,
            },
            blockExplorerUrls: [
              targetConfig.chainId === ChainId.AVALANCHE ? 'https://snowtrace.io/' : 'https://testnet.snowtrace.io/',
            ],
          }

          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [networkParams],
          })
          return true
        } catch (addError) {
          console.error('Failed to add network:', addError)
          return false
        }
      }
      console.error('Failed to switch network:', switchError)
      return false
    }
  }

  const value: SwapNetworkContext = {
    provider,
    config: currentNetworkConfig,
    switchToSwapNetwork,
    canSwitchNetworks,
    availableNetworks,
    switchToNetwork,
  }

  return <SwapNetworkContext.Provider value={value}>{children}</SwapNetworkContext.Provider>
}

export function useSwapNetwork(): SwapNetworkContext {
  const context = useContext(SwapNetworkContext)
  if (!context) {
    throw new Error('useSwapNetwork must be used within a SwapNetworkProvider')
  }
  return context
}
